// useHomeSnapshot — feeds the four-page swipeable snapshot panel on
// Home. Returns four short arrays (top 3 each) plus a total count
// per category for the "Show more (N) →" affordance.
//
// Categories:
//   cooks    — cook_logs from self + family (delegates to useActivityFeed)
//   kitchen  — pantry_items inserts in last 14 days for self + family
//   recipes  — user_recipes inserts in last 30 days for self + family
//   calendar — scheduled_meals scheduled within next 30 days, ascending
//
// Why a thin compose-hook: keeps the two existing hooks
// (useActivityFeed + useScheduledMeals) doing what they already do,
// adds two narrow Supabase reads for kitchen + recipes. Realtime
// is best-effort: cooks ride useActivityFeed's channel; the rest
// re-fetches on a 60s poll plus when familyIds change. That's fine
// for a Home snapshot — if a family member adds a recipe right now,
// it'll show within a minute.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabase";
import { useActivityFeed } from "./useActivityFeed";
import { useScheduledMeals } from "./useScheduledMeals";

const HOUR_MS = 60 * 60 * 1000;

export function useHomeSnapshot({ userId, familyIds = [] }) {
  // ── Cooks — leverage the existing feed; filter kind="cook" ──────
  const activityFeed = useActivityFeed(userId, familyIds, 30);
  const cooks = useMemo(() => {
    return (activityFeed.items || [])
      .filter(it => it.kind === "cook")
      .slice(0, 3);
  }, [activityFeed.items]);
  const cooksTotal = useMemo(
    () => (activityFeed.items || []).filter(it => it.kind === "cook").length,
    [activityFeed.items],
  );

  // ── Calendar — upcoming scheduled meals in next 30 days ────────
  // EGRESS GUARDRAIL: bucket `now` to the minute so the fromISO /
  // toISO strings are stable across re-renders within the same
  // minute. Without this, useScheduledMeals re-fetches on every
  // Home re-render (carousel ticks, theme transitions, etc.). The
  // 30-day calendar window doesn't care about sub-minute drift.
  const minuteBucket = Math.floor(Date.now() / 60000);
  const calFromISO = useMemo(
    () => new Date(minuteBucket * 60000 - HOUR_MS).toISOString(),
    [minuteBucket],
  );
  const calToISO = useMemo(
    () => new Date(minuteBucket * 60000 + 30 * 24 * HOUR_MS).toISOString(),
    [minuteBucket],
  );
  const { meals: scheduledMeals = [] } = useScheduledMeals(userId, {
    fromISO: calFromISO,
    toISO:   calToISO,
  }) || {};
  const calendar = useMemo(() => (scheduledMeals || []).slice(0, 3), [scheduledMeals]);
  const calendarTotal = (scheduledMeals || []).length;

  // ── Kitchen updates — pantry_items inserts in last 14 days ─────
  const [kitchenItems, setKitchenItems] = useState([]);
  // ── New recipes — user_recipes inserts in last 30 days ─────────
  const [recipeItems, setRecipeItems] = useState([]);

  // Visible authors: self + accepted family.
  const visibleIds = useMemo(() => {
    const set = new Set([userId, ...(familyIds || [])].filter(Boolean));
    return [...set];
  }, [userId, familyIds]);

  useEffect(() => {
    if (!userId || visibleIds.length === 0) return undefined;
    let alive = true;
    const since14 = new Date(Date.now() - 14 * 24 * HOUR_MS).toISOString();
    const since30 = new Date(Date.now() - 30 * 24 * HOUR_MS).toISOString();

    const load = async () => {
      try {
        const [pantryRes, recipeRes] = await Promise.all([
          supabase
            .from("pantry_items")
            .select("id,user_id,name,emoji,brand,canonical_id,created_at")
            .in("user_id", visibleIds)
            .gte("created_at", since14)
            .order("created_at", { ascending: false })
            .limit(20),
          supabase
            .from("user_recipes")
            .select("id,user_id,slug,source,recipe,created_at")
            .in("user_id", visibleIds)
            .gte("created_at", since30)
            .order("created_at", { ascending: false })
            .limit(20),
        ]);
        if (!alive) return;
        if (!pantryRes.error) setKitchenItems(pantryRes.data || []);
        if (!recipeRes.error) setRecipeItems(recipeRes.data || []);
      } catch (e) {
        // best-effort; surface nothing if the query failed
        console.warn("[useHomeSnapshot] load threw:", e?.message || e);
      }
    };
    load();
    // Visibility-gated 60s poll — pauses when the tab is backgrounded
    // so a Home tab left open in the background isn't burning egress
    // for nothing. Returns to foreground triggers an immediate load
    // so the snapshot is fresh when the user comes back.
    let id = null;
    const start = () => {
      if (id) return;
      id = setInterval(load, 60_000);
    };
    const stop = () => {
      if (id) { clearInterval(id); id = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, visibleIds.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    cooks,        cooksTotal,
    kitchen:      kitchenItems.slice(0, 3), kitchenTotal: kitchenItems.length,
    recipes:      recipeItems.slice(0, 3),  recipesTotal: recipeItems.length,
    calendar,     calendarTotal,
    loading:      activityFeed.loading,
    refresh:      activityFeed.refresh,
  };
}
